// Inspect MP4 timing locally: no upload, decoding or paid API call.
// Unknown/fragmented layouts deliberately keep the manual confirmation fallback.
export function inspectMp4Timing(buffer) {
  const v=new DataView(buffer),text=(p,n)=>String.fromCharCode(...new Uint8Array(buffer,p,n));
  const boxes=(start,end)=>{
    const result=[];
    for(let p=start;p+8<=end;){
      let size=v.getUint32(p),header=8;
      if(size===1){if(p+16>end)throw Error();size=Number(v.getBigUint64(p+8));header=16;}
      if(size===0)size=end-p;
      if(!Number.isSafeInteger(size)||size<header||p+size>end)throw Error();
      result.push({type:text(p+4,4),start:p+header,end:p+size});p+=size;
    }return result;
  };
  const child=(box,type)=>boxes(box.start,box.end).find(b=>b.type===type);
  try{
    const top=boxes(0,v.byteLength),moov=top.find(b=>b.type==='moov');
    if(!moov||top.some(b=>b.type==='moof')||child(moov,'mvex'))return null;
    for(const trak of boxes(moov.start,moov.end).filter(b=>b.type==='trak')){
      const mdia=child(trak,'mdia');if(!mdia)continue;
      const hdlr=child(mdia,'hdlr');if(!hdlr||text(hdlr.start+8,4)!=='vide')continue;
      const mdhd=child(mdia,'mdhd'),minf=child(mdia,'minf'),stbl=minf&&child(minf,'stbl');
      const stts=stbl&&child(stbl,'stts');if(!mdhd||!stts)return null;
      const scale=v.getUint32(mdhd.start+(v.getUint8(mdhd.start)===1?20:12));
      const count=v.getUint32(stts.start+4),durations=[];
      if(!scale||stts.start+8+count*8>stts.end)return null;
      for(let i=0;i<count;i++){
        const p=stts.start+8+i*8,n=v.getUint32(p),delta=v.getUint32(p+4);
        if(!delta||durations.length+n>1200000)return null;
        for(let j=0;j<n;j++)durations.push(delta);
      }
      if(durations.length<2)return null;
      const offsets=new Array(durations.length).fill(0),ctts=child(stbl,'ctts');
      if(ctts){
        const n=v.getUint32(ctts.start+4);if(ctts.start+8+n*8>ctts.end)return null;
        let k=0;
        for(let i=0;i<n;i++){
          const p=ctts.start+8+i*8,c=v.getUint32(p),offset=v.getUint8(ctts.start)===1?v.getInt32(p+4):v.getUint32(p+4);
          if(k+c>offsets.length)return null;
          for(let j=0;j<c;j++)offsets[k++]=offset;
        }if(k!==offsets.length)return null;
      }
      let clock=0;
      const pts=durations.map((delta,i)=>{const value=clock+offsets[i];clock+=delta;return value;}).sort((a,b)=>a-b);
      const delta=pts[1]-pts[0];
      if(delta<=0)return {frameStatus:'variable-fps'};
      // MP4 writes fractional NTSC rates (e.g. 30000/1001) by alternating
      // adjacent integer timescale ticks. That is timestamp QUANTISATION,
      // not VFR. Accept only when every PTS sits on one exact CFR lattice
      // within two ticks; a genuinely drifting or dropped frame still fails.
      let quantisedRate=null;
      let low=Infinity,high=0,dLow=Infinity,dHigh=0;
      for(let i=1;i<pts.length;i++){
        const spacing=pts[i]-pts[i-1];
        if(spacing<low)low=spacing;
        if(spacing>high)high=spacing;
      }
      for(const durationTicks of durations){
        if(durationTicks<dLow)dLow=durationTicks;
        if(durationTicks>dHigh)dHigh=durationTicks;
      }
      if(low<=0||high-low>1||dHigh-dLow>1)return {frameStatus:'variable-fps'};
      const commonRates=[[24000,1001],[24,1],[25,1],[30000,1001],[30,1],
        [50,1],[60000,1001],[60,1],[100,1],[120,1],[120000,1001],[240,1]];
      if(high===low && dHigh===dLow){
        const gcd=(a,b)=>b?gcd(b,a%b):a,g=gcd(scale,delta);
        const num=scale/g,den=delta/g;
        if([1,1001].includes(den)&&num/den>=1&&num/den<=240)
          quantisedRate=[num,den];
      }
      if(!quantisedRate){
        quantisedRate=commonRates.find(([num,den])=>{
          const ticksPerFrame=scale*den/num;
          if(Math.abs((pts.at(-1)-pts[0])/(pts.length-1)-ticksPerFrame)>0.05)return false;
          return pts.every((p,i)=>Math.abs(p-pts[0]-i*ticksPerFrame)<=2);
        });
      }
      if(!quantisedRate)return {frameStatus:'variable-fps'};
      const edts=child(trak,'edts'),elst=edts&&child(edts,'elst');
      if(elst){
        if(v.getUint32(elst.start+4)!==1)return null;
        const version=v.getUint8(elst.start),mediaTime=version===1?Number(v.getBigInt64(elst.start+16)):v.getInt32(elst.start+12);
        if(mediaTime!==pts[0])return null;
      }
      const [fpsNum,fpsDen]=quantisedRate;
      return {fpsNum,fpsDen,frameStatus:'detected-cfr',frameCount:durations.length};
    }
  }catch{/* Unsupported or malformed metadata: never guess. */}
  return null;
}

/**
 * Inspect MP4 timing without reading gigabytes of camera media into RAM.
 * Reads container box headers and only the moov metadata (max 48 MiB).
 * Unsupported / fragmented / variable-FPS MP4s fail closed for proxy creation.
 */
export async function inspectMp4TimingFile(file) {
 if(!file || !Number.isSafeInteger(file.size) || file.size<16)return null;
 let position=0;
 for(let boxes=0;position+8<=file.size&&boxes<10000;boxes++){
  const header=await file.slice(position,Math.min(file.size,position+16)).arrayBuffer();
  const view=new DataView(header);
  let size=view.getUint32(0),headerSize=8;
  if(size===1){if(header.byteLength<16)return null;size=Number(view.getBigUint64(8));headerSize=16;}
  if(size===0)size=file.size-position;
  if(!Number.isSafeInteger(size)||size<headerSize||position+size>file.size)return null;
  const type=String.fromCharCode(...new Uint8Array(header,4,4));
  if(type==='moof')return null;
  if(type==='moov'){
   if(size>48*1024*1024)return null;
   return inspectMp4Timing(await file.slice(position,position+size).arrayBuffer());
  }
  position+=size;
 }
 return null;
}
